import { defineNuxtPlugin, useRuntimeConfig } from '#app';

// Loads Meta (Facebook) Pixel on the client after mount. Wraps PageView
// tracking for both initial load and SPA navigations. Custom conversion
// events (Lead, ViewContent, InitiateCheckout) are wired via the
// useAnalytics composable + meta-event mapper added in a follow-up step.

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      push?: unknown;
      loaded?: boolean;
      version?: string;
    };
    _fbq?: Window['fbq'];
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // Skip in dev so local traffic doesn't pollute the dataset.
  if (!import.meta.env.PROD) return;

  // Skip on operator/closed routes — see utils/analytics-suppressed.ts.
  if (isAnalyticsSuppressed(location.pathname)) return;

  const { metaPixelId } = useRuntimeConfig().public as {
    metaPixelId?: string;
  };
  if (!metaPixelId) return;

  // Stub queue installed synchronously — fbq calls before fbevents.js
  // finishes loading replay through the queue on init.
  if (!window.fbq) {
    const stub = function (...args: unknown[]) {
      // @ts-expect-error - Meta's official snippet shape
      if (stub.callMethod) stub.callMethod(...args);
      // @ts-expect-error - queue is part of Meta's snippet contract
      else (stub.queue = stub.queue || []).push(args);
    } as Window['fbq'];
    window.fbq = stub;
    if (!window._fbq) window._fbq = stub;
    if (window.fbq) {
      window.fbq.queue = [];
      window.fbq.loaded = true;
      window.fbq.version = '2.0';
    }
  }

  // Init + initial PageView. Both queue up and replay once fbevents.js
  // arrives, so calling them before script injection is fine.
  window.fbq?.('init', metaPixelId);
  window.fbq?.('track', 'PageView');

  // SPA navigations — Pixel doesn't auto-fire PageView on route change,
  // so we hook router.afterEach to keep funnels intact across client-side
  // navigations (e.g. /go → /search → /apartment/N).
  nuxtApp.hook('page:finish', () => {
    window.fbq?.('track', 'PageView');
  });

  // Lazy-load fbevents.js — то же решение что и для YM в analytics.client.ts
  // (2026-05-26): tag.js + Pixel recorder грузятся только после первого
  // user interaction. Lighthouse throttled показал ~164 ms long-tasks
  // от fbevents.js + signals config в LCP-окне; lazy-инжект убирает их.
  // PageView события поставлены в очередь синхронно выше — flushнутся
  // когда script загрузится. Trade-off: pure bouncers без interaction не
  // регистрируются в Pixel → FB Ad Manager под-атрибутирует landing-page
  // funnels для не-конвертирующих пользователей. Conversion events (Lead,
  // InitiateCheckout) фигурируют только после явного действия, поэтому
  // к моменту их fire script уже загружен. Safety-net 15 s ловит "тихих"
  // visitors что читают hero без скролла.
  const injectScript = () => {
    const src = 'https://connect.facebook.net/en_US/fbevents.js';
    const already = Array.from(document.scripts).some((s) => s.src === src);
    if (already) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = src;
    document.head.appendChild(s);
  };

  const scheduleInject = () => {
    let fired = false;
    const events = ['scroll', 'click', 'keydown', 'touchstart', 'pointerdown'] as const;
    const opts = { capture: true } as const;
    const fire = () => {
      if (fired) return;
      fired = true;
      events.forEach((e) => window.removeEventListener(e, fire, opts));
      clearTimeout(timeoutId);
      injectScript();
    };
    events.forEach((e) =>
      window.addEventListener(e, fire, { once: true, passive: true, capture: true }),
    );
    const timeoutId = setTimeout(fire, 15000);
  };

  if (document.readyState === 'complete') {
    scheduleInject();
  } else {
    window.addEventListener('load', scheduleInject, { once: true });
  }
});
